import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import { apiPath } from 'presentations/routes/path';
import { get } from 'infras/rest_client';
import Subscription from 'domains/models/subscription';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import { AccountListItem } from 'presentations/api/schema/account';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';

import {
  IntegrationType,
  ProviderType,
} from 'domains/models/account/providerType';
import { ViewerItem } from 'presentations/api/schema/user';
import { Role } from 'domains/models/team_user/role';
import CalendarIntegrationFeature from 'domains/models/subscription/features/integrations/calendar';
import { isiOSApp } from 'presentations/views/helpers/electron';

interface Props {
  viewer: ViewerItem;
}

export const useIntegrationListPage = (props: Props) => {
  const [isInitLoading, setInitLoading] = useState<boolean>(false);
  const [
    subscriptionState,
    setSubscriptionState,
  ] = useState<SubscriptionStateItem | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<AccountListItem[]>(
    [],
  );
  const [
    shopAccount,
    setShopAccount,
  ] = useState<IntegrationMergedTeamShopAccountItem | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const router = useRouter();

  useEffect(() => {
    fetch();
  }, []);

  const fetch = async () => {
    setInitLoading(true);
    let result = await get(apiPath('/v1/accounts'));
    if (!result.isSuccess) {
      setInitLoading(false);
      return;
    }
    setSelectedAccounts(result.data);

    const shopAccountResult = await get(
      apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account`),
    );
    if (!shopAccountResult.isSuccess && shopAccountResult.status !== 404) {
      setInitLoading(false);
      return;
    }
    setShopAccount(shopAccountResult.data);

    result = await get(
      apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
    );
    if (!result.isSuccess) {
      setInitLoading(false);
      return;
    }
    setSubscriptionState(result.data);
    setInitLoading(false);
  };

  const onCloseDialog = useCallback((e: any) => {
    setIsOpenDialog(false);
  }, []);

  const checkAdminRole = (e: any) => {
    if (props.viewer.role > Role.Admin) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpenDialog(true);
      return;
    }
  };
  const providerTypes = useMemo(() => {
    const types = [];
    for (const account of selectedAccounts) {
      switch (account.providerType) {
        case ProviderType.Google:
          types.push(IntegrationType.GoogleCalendar);
          types.push(IntegrationType.GoogleMeet);
          break;
        case ProviderType.Microsoft:
          types.push(IntegrationType.Outlook);
          break;
        case ProviderType.Slack:
          types.push(IntegrationType.Slack);
          break;
        case ProviderType.Zoom:
          types.push(IntegrationType.Zoom);
          break;
        case ProviderType.Apple:
          types.push(IntegrationType.iCloudCalendar);
          break;
        case ProviderType.Facebook:
          types.push(IntegrationType.Facebook);
          break;
        case ProviderType.Teams:
          types.push(IntegrationType.Teams);
          break;
        case ProviderType.Salesforce:
          types.push(IntegrationType.Salesforce);
          break;
        case ProviderType.HubSpot:
          types.push(IntegrationType.HubSpot);
          break;
        case ProviderType.Notion:
          types.push(IntegrationType.Notion);
          types.push(IntegrationType.NotionForm);
          break;
        case ProviderType.GoogleSheet:
          types.push(IntegrationType.GoogleSheet);
          break;
        default:
          types.push(IntegrationType.None);
          break;
      }
    }
    return types;
  }, [selectedAccounts]);

  let canAddCalendar = true;
  if (subscriptionState) {
    const calendarIntegrationFeature = new CalendarIntegrationFeature({
      planVersion: subscriptionState.planVersion,
    });
    canAddCalendar =
      calendarIntegrationFeature.maxCount(subscriptionState.planId) >
      subscriptionState.calendarCount;
  }
  const isShowAdvancedMenu = Subscription.isShowIntegrationAdvancedMenu(
    props.viewer.planId,
    props.viewer.role,
  );

  return {
    state: {
      isLoading: isInitLoading,
      selectedProviderTypes: providerTypes,
      canAddCalendar,
      isShowAdvancedMenu,
      shopAccount,
      isOpenDialog,
      isiOSApp: isiOSApp(),
    },
    actions: {
      onCloseDialog,
      checkAdminRole,
    },
  };
};
